/**
 * StripeSubscriptionsInspector
 * ----------------------------
 * Live read-straight-from-Stripe of every subscription on a client's
 * stripe_customer, paired with DB drift detection. Surfaces:
 *   - Orphan in Stripe   → Stripe is charging but our DB has no record
 *   - Stale DB           → DB says cancelled, Stripe still billing
 *   - Missing in Stripe  → DB says active, Stripe has no record
 *   - Cancelled in Stripe → DB still active, Stripe already stopped billing
 *
 * Owner spec 2026-02-17 — "Comprehensive Money Flow UI" (preventing
 * doubled-up subscription confusion).
 *
 * Backend:
 *   GET  /api/coach/clients/{user_id}/stripe-subscriptions
 *   POST /api/coach/clients/{user_id}/stripe-subscriptions/{stripe_sub_id}/cancel
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    AlertTriangle, Loader2, Search, ShieldAlert, X, ChevronDown, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) =>
    cents == null ? "—" : `$${(cents / 100).toFixed(2)}`;
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short", day: "numeric", year: "numeric",
        });
    } catch { return iso; }
};

const DRIFT_KIND_META = {
    orphan_in_stripe: {
        label: "Orphan in Stripe",
        tone: "bg-red-950/40 border-red-700/60 text-red-200",
        icon: ShieldAlert,
    },
    stale_db: {
        label: "DB out of sync (still billing)",
        tone: "bg-amber-950/40 border-amber-700/60 text-amber-200",
        icon: AlertTriangle,
    },
    missing_in_stripe: {
        label: "DB ghost (not in Stripe)",
        tone: "bg-zinc-900 border-zinc-700 text-zinc-300",
        icon: AlertTriangle,
    },
    cancelled_in_stripe: {
        label: "Cancelled in Stripe, active in DB",
        tone: "bg-amber-950/40 border-amber-700/60 text-amber-200",
        icon: AlertTriangle,
    },
};

function DriftRow({ userId, drift, onChanged }) {
    const [busy, setBusy] = useState(false);
    const meta = DRIFT_KIND_META[drift.kind] || DRIFT_KIND_META.orphan_in_stripe;
    const Icon = meta.icon;
    const canCancelInStripe = drift.kind === "orphan_in_stripe" || drift.kind === "stale_db";

    const doCancel = async (atPeriodEnd) => {
        const verb = atPeriodEnd ? "Cancel at period end" : "Cancel NOW (immediate)";
        const msg = `${verb}?\n\nStripe Sub: ${drift.stripe_subscription_id}\n${fmtMoney(drift.price_cents)}/${drift.interval || "?"}\n\nThis stops Stripe from billing them.`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            await axios.post(
                `${API}/coach/clients/${userId}/stripe-subscriptions/${drift.stripe_subscription_id}/cancel`,
                { at_period_end: atPeriodEnd, reason: "orphan_cleanup" },
                { withCredentials: true },
            );
            toast.success(atPeriodEnd ? "Will cancel at period end" : "Cancelled immediately");
            onChanged();
        } catch (e) {
            toast.error("Cancel failed", { description: e?.response?.data?.detail || "Unknown error" });
        } finally { setBusy(false); }
    };

    return (
        <div
            data-testid={`stripe-drift-row-${drift.stripe_subscription_id}`}
            className={`border ${meta.tone} px-3 py-3 mb-2`}
        >
            <div className="flex items-start gap-2">
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-[10px] tracking-[0.2em] uppercase font-semibold">
                            {meta.label}
                        </span>
                        <code className="text-[10px] text-zinc-400 font-mono break-all">
                            {drift.stripe_subscription_id}
                        </code>
                    </div>
                    <p className="text-xs mt-1.5 leading-snug">{drift.detail}</p>
                    {(drift.price_cents != null || drift.interval) && (
                        <div className="text-[11px] text-zinc-400 mt-1.5 font-mono">
                            {fmtMoney(drift.price_cents)}{drift.interval ? `/${drift.interval}` : ""}
                            {drift.plan_nickname ? ` · ${drift.plan_nickname}` : ""}
                            {drift.current_period_end ? ` · next: ${fmtDate(drift.current_period_end)}` : ""}
                        </div>
                    )}
                    {canCancelInStripe && (
                        <div className="flex items-center gap-2 mt-2">
                            <button
                                type="button"
                                data-testid={`stripe-drift-cancel-end-${drift.stripe_subscription_id}`}
                                onClick={() => doCancel(true)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-amber-700/60 bg-amber-900/40 text-amber-200 hover:bg-amber-800/50 px-2.5 py-1.5 disabled:opacity-50"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                                End at period
                            </button>
                            <button
                                type="button"
                                data-testid={`stripe-drift-cancel-now-${drift.stripe_subscription_id}`}
                                onClick={() => doCancel(false)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-red-700/60 bg-red-900/40 text-red-200 hover:bg-red-800/50 px-2.5 py-1.5 disabled:opacity-50"
                                title="Cancel immediately in Stripe — no refund"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                                Cancel NOW
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function StripeSubscriptionsInspector({ userId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const r = await axios.get(
                `${API}/coach/clients/${userId}/stripe-subscriptions`,
                { withCredentials: true },
            );
            setData(r.data || null);
        } catch (e) {
            toast.error("Couldn't load live Stripe data", {
                description: e?.response?.data?.detail || "Unknown error",
            });
            setData(null);
        } finally { setLoading(false); }
    }, [userId]);

    // Lazy load — only fire when the user expands
    useEffect(() => {
        if (open && data == null && !loading) load();
    }, [open, data, loading, load]);

    const driftCount = data?.drift?.length || 0;
    const liveCount = data?.live_subscriptions?.length || 0;

    return (
        <section
            data-testid="stripe-inspector-panel"
            className="border border-zinc-800 bg-zinc-950/40 p-4 mt-4"
        >
            <button
                type="button"
                data-testid="stripe-inspector-toggle"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 text-left"
            >
                <div className="flex items-center gap-2">
                    {open
                        ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
                        : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />}
                    <Search className="w-3.5 h-3.5 text-zinc-400" />
                    <span className="text-[11px] tracking-[0.28em] uppercase text-zinc-300 font-display font-bold">
                        Stripe Drift Inspector
                    </span>
                    {data && driftCount > 0 && (
                        <span
                            data-testid="stripe-drift-badge"
                            className="text-[10px] tracking-[0.2em] uppercase bg-red-900/60 text-red-200 px-2 py-0.5 border border-red-700/60"
                        >
                            {driftCount} drift{driftCount === 1 ? "" : "s"}
                        </span>
                    )}
                </div>
                <span className="text-[10px] text-zinc-500">
                    {open ? "Hide" : "Show live Stripe data"}
                </span>
            </button>
            {open && (
                <div className="mt-3">
                    {loading && (
                        <div className="text-xs text-zinc-500 flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Fetching live from Stripe…
                        </div>
                    )}
                    {!loading && data && data.note && (
                        <p className="text-xs text-zinc-500">{data.note}</p>
                    )}
                    {!loading && data && data.stripe_customer_id && (
                        <>
                            <div className="text-[10px] text-zinc-500 mb-2 font-mono break-all">
                                customer: {data.stripe_customer_id}
                            </div>
                            {driftCount === 0 ? (
                                <div
                                    data-testid="stripe-drift-clean"
                                    className="border border-emerald-700/40 bg-emerald-900/10 text-emerald-200 px-3 py-2.5 text-xs"
                                >
                                    ✓ DB matches Stripe — no drift on {liveCount} subscription{liveCount === 1 ? "" : "s"}.
                                </div>
                            ) : (
                                <>
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-red-300 mb-2">
                                        ⚠ Drift detected — review before clients are double-charged
                                    </div>
                                    {data.drift.map((d) => (
                                        <DriftRow
                                            key={d.stripe_subscription_id}
                                            userId={userId}
                                            drift={d}
                                            onChanged={load}
                                        />
                                    ))}
                                </>
                            )}
                            <div className="text-[10px] text-zinc-500 mt-2">
                                {liveCount} live Stripe sub{liveCount === 1 ? "" : "s"} (incl. cancelled) ·
                                {" "}{data.db_subscriptions?.length || 0} DB row{(data.db_subscriptions?.length || 0) === 1 ? "" : "s"}
                            </div>
                            <button
                                type="button"
                                data-testid="stripe-inspector-refresh"
                                onClick={load}
                                disabled={loading}
                                className="mt-2 text-[10px] tracking-[0.15em] uppercase text-zinc-400 hover:text-white"
                            >
                                ↻ Refresh
                            </button>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}
